<template>
  <d-container
    fluid
    class="main-content-container">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <h3 class="page-title">
          {{ $t('shipments.shipmentsList') }}
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          v-if="!userIsCustomer"
          theme="success"
          @click.prevent="addShipmentUPS()">
          <i class="material-icons">add</i>
          {{ $t('shipments.addNewShipmentUPS') }}
        </d-button>

        <d-button
          v-if="!userIsCustomer"
          theme="success"
          :outline="true"
          class="ml-2"
          @click.prevent="addShipmentPOD()">
          <i class="material-icons">add</i>
          {{ $t('shipments.addNewShipmentPOD') }}
        </d-button>
      </d-col>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0" style="width: 120px;">{{ $t('shipments.status') }}</th>
            <th scope="col" class="border-0" style="width: 120px;">{{ $t('shipments.shipper') }}</th>
            <th scope="col" class="border-0" style="width: 200px;">{{ $t('shipments.trackingNumber') }}</th>
            <th scope="col" class="border-0" style="width: 200px">{{ $t('shipments.createdAt') }}</th>
            <th scope="col" class="border-0">{{ $t('shipments.createdBy') }}</th>
            <th scope="col" class="border-0" style="width: 200px;">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="!loadError">
          <tr
            v-for="(shipment, index) of shipments"
            :key="'shipment-' + index">
            <td class="align-middle">
              <d-badge
                v-if="shipment.tracking_status"
                :theme="trackingStatusBadgeTheme[shipment.tracking_status]"
                class="mb-2"
                :title="$t('order.lastChange') + ': ' + shipment.tracking_status_at">
                {{ $t('shipments.trackingStatuses.' + shipment.tracking_status) }}
              </d-badge>

              <br v-if="shipment.tracking_status" />

              <d-badge
                :outline="true"
                :theme="statusBadgeTheme[shipment.status]">
                {{ $t('shipments.statuses.' + shipment.status.toLowerCase()) }}
              </d-badge>
            </td>
            <td class="align-middle">
              {{ shipment.shipper }}
            </td>
            <td class="align-middle">
              <template v-if="shipment.shipper === 'DPD' || shipment.manually_pod">
                {{ shipment.tracking_number }}
              </template>
              <template v-else>
                <small v-if="shipment.packages.length">
                  <span
                    v-for="(pkg, index) of shipment.packages"
                    :key="'package-' + shipment.id + '-' + index">
                    {{ pkg.tracking_number }}<br>
                  </span>
                </small>
                <small v-else>
                  {{ $t('ui.lack') }}
                </small>
              </template>

              <small class="d-block text-muted">
                {{ $t('shipments.estimateDeliveryDate') }}:
                <template v-if="shipment.estimate_delivery_date">{{ shipment.estimate_delivery_date }}</template>
                <template v-else>{{ $t('ui.lack') }}</template>
              </small>
            </td>
            <td class="align-middle">
              {{ shipment.created_at }}
            </td>
            <td class="align-middle">
              {{ shipment.user_name }}
            </td>
            <td class="align-middle">
              <d-dropdown
                v-if="shipment.status === 'INTEGRATED' && !userIsCustomer && (shipment.shipper !== 'DPD' || (shipment.shipper === 'DPD' && shipment.can_generate_pod))"
                icon="cloud_download"
                theme="info"
                :disabled="downloadingLabel !== false || downloadingPod !== false"
                class="mr-2">
                <d-dropdown-item v-if="shipment.shipper !== 'DPD' && shipment.label_url">
                  <a
                    :disabled="downloadingLabel !== false"
                    @click.prevent="downloadLabel(shipment.id)"
                    :style="'display: block;' + ((downloadingLabel !== false) ? 'pointer-events: none;' : '')">
                    <template v-if="downloadingLabel !== shipment.id">{{ $t('shipments.downloadLabelForShipment') }}</template>
                    <template v-else>
                      <div class="spinner-border spinner-border-sm" role="status"></div>
                    </template>
                  </a>
                </d-dropdown-item>
                <d-dropdown-item v-if="shipment.can_generate_pod">
                  <a
                    :disabled="downloadingPod !== false"
                    @click.prevent="downloadPod(shipment.pod_url, shipment.id)"
                    :style="'display: block;' + ((downloadingPod !== false) ? 'pointer-events: none;' : '')">
                    <template v-if="downloadingPod !== shipment.id">{{ $t('shipments.downloadPodForShipment') }}</template>
                    <template v-else>
                      <div class="spinner-border spinner-border-sm" role="status"></div>
                    </template>
                  </a>
                </d-dropdown-item>
              </d-dropdown>

              <d-button
                v-if="shipment.shipper !== 'DPD'"
                theme="primary"
                @click.prevent="openViewPopup(shipment.id)"
                class="mr-2"
                :title="$t('shipments.viewShipment')">
                <i class="material-icons">visibility</i>
              </d-button>

              <d-button
                v-if="!userIsCustomer"
                theme="danger"
                :disabled="cancellingShipment !== false || shipment.status === 'CANCELLED' || !shipment.can_cancel"
                @click.prevent="cancelShipment(shipment.id)"
                :title="$t('shipments.cancelShipment')">
                <template v-if="cancellingShipment !== shipment.id">
                  <i class="material-icons">cancel</i>
                </template>
                <template v-else>
                  <div class="spinner-border spinner-border-sm" role="status"></div>
                </template>
              </d-button>
            </td>
          </tr>
          <tr v-if="shipments.length === 0">
            <td
              colspan="6"
              class="text-center">
              {{ $t('shipments.noShipmentsToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />

    <shipment-add-popup
      v-if="showAddPopupUPS"
      :orderID="orderID"
      :orderName="orderName"
      :addressData="addressData"
      :contactData="contactData"
      :customerID="customerID"
      :customerData="customerData"
      :customerContactData="customerContactData"
      :orderDescription="orderDescription" />

    <shipment-add-pod-popup
      v-if="showAddPopupPOD"
      :orderID="orderID" />

    <shipment-view-popup
      v-if="showViewPopup"
      :shipmentID="selectedShipmentID" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import CustomerPanel from './../../mixins/CustomerPanel.vue';
import HasPagination from './../../mixins/HasPagination.vue';
import ListUtils from './../../utils/ListUtils.js';
import Pagination from './../common/Pagination.vue';
import ShipmentAddPopup from './../shipments/ShipmentAddPopup.vue';
import ShipmentAddPodPopup from './../shipments/ShipmentAddPodPopup.vue';
import ShipmentViewPopup from './../shipments/ShipmentViewPopup.vue';
import Vue from 'vue';

export default {
  name: 'order-shipments',
  mixins: [
    CustomerPanel,
    HasPagination
  ],
  props: [
    'orderID',
    'orderName',
    'addressData',
    'contactData',
    'customerID',
    'customerData',
    'customerContactData',
    'orderDescription',
    'addressConfirmed'
  ],
  components: {
    'pagination': Pagination,
    'shipment-add-popup': ShipmentAddPopup,
    'shipment-add-pod-popup': ShipmentAddPodPopup,
    'shipment-view-popup': ShipmentViewPopup
  },
  computed: {
    allDataLoaded () {
      return this.loaded;
    },
    statusBadgeTheme () {
      return {
        NEW: 'primary',
        ERROR: 'danger',
        INTEGRATED: 'info',
        CANCELLED: 'dark',
        DELIVERED: 'success'
      };
    },
    trackingStatusBadgeTheme () {
      return {
        I: 'warning',
        D: 'success',
        X: 'danger',
        P: 'info',
        M: 'dark'
      };
    },
    addressDataSet () {
      return !!this.addressData.receiver_address_postal_code || !!this.addressData.receiver_address_country_code;
    },
    contactDataSet () {
      return !!this.contactData.receiver_attention_name;
    }
  },
  data() {
    return {
      shipments: [],
      loaded: false,
      loadError: false,
      downloadingLabel: false,
      downloadingPod: false,
      cancellingShipment: false,
      showAddPopupUPS: false,
      showAddPopupPOD: false,
      showViewPopup: false,
      selectedShipmentID: null
    };
  },
  mounted () {
    this.loadFilteredData();
    this.$bus.$on('shipments-reload', this.debouncedLoadFilteredData);
    this.$bus.$on('add-shipment-popup-close', this.closeShipmentAddPopup);
    this.$bus.$on('add-pod-popup-close', this.closePODAddPopup);
    this.$bus.$on('view-shipment-popup-close', this.closeShipmentViewPopup);
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {
        order_id: this.orderID
      };

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: this.getApiEndpoint('/shipments/items?page=' + this.currentPage),
        listField: 'shipments',
        params: {
          show_all: true,
          where
        }
      });
    },
    downloadLabel (shipmentID) {
      this.downloadingLabel = shipmentID;

      this.$http.post('/shipments/download-label/' + shipmentID, null, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filenamePrefix = this.$t('shipments.labelFilePrefix');
        link.setAttribute('download', filenamePrefix + shipmentID + '.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingLabel = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingLabel = false;
      });
    },
    downloadPod (podUrl, shipmentID) {
      this.downloadingPod = shipmentID;

      this.$http.post(podUrl, null, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filenamePrefix = this.$t('shipments.podFilePrefix');
        link.setAttribute('download', filenamePrefix + shipmentID + '.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingPod = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingPod = false;
      });
    },
    cancelShipment (shipmentID) {
      this.cancellingShipment = shipmentID;

      this.$http.post('/shipments/cancel/' + shipmentID, null).then(response => {
        if (response && response.data && response.data.success) {
          Vue.swal({
            title: this.$t('shipments.shipmentCancelled'),
            html: this.$t('shipments.shipmentCancelledDesc'),
            type: 'success',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });

          this.cancellingShipment = false;
          this.$bus.$emit('shipments-reload');
        } else {
          Vue.swal({
            title: this.$t('shipments.shipmentCancelError'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });

          this.cancellingShipment = false;
        }
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('shipments.shipmentCancelError'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.cancellingShipment = false;
      });
    },
    closeShipmentAddPopup () {
      this.showAddPopupUPS = false;
    },
    closePODAddPopup () {
      this.showAddPopupPOD = false;
    },
    closeShipmentViewPopup () {
      this.showViewPopup = false;
    },
    openViewPopup (shipmentID) {
      this.selectedShipmentID = shipmentID;
      this.showViewPopup = true;
    },
    addShipmentUPS () {
      if (!this.addressIsConfirmed()) {
        return;
      }

      if (!this.addressDataSet || !this.contactDataSet) {
        Vue.swal({
          title: this.$t('shipments.lackOfAddressContactData'),
          html: this.$t('shipments.lackOfAddressContactDataDesc'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        return;
      }

      this.showAddPopupUPS = true;
    },
    addShipmentPOD () {
      if (!this.addressIsConfirmed()) {
        return;
      }

      this.showAddPopupPOD = true;
    },
    addressIsConfirmed () {
      if (this.addressConfirmed) {
        return true;
      }

      Vue.swal({
        title: this.$t('shipments.addressNotConfirmed'),
        html: this.$t('shipments.addressNotConfirmedDesc'),
        type: 'warning',
        confirmButtonText: this.$t('ui.ok'),
        buttonsStyling: true
      });

      return false;
    }
  },
  beforeDestroy () {
    this.$bus.$off('shipments-reload', this.debouncedLoadFilteredData);
    this.$bus.$off('add-shipment-popup-close', this.closeShipmentAddPopup);
    this.$bus.$off('add-pod-popup-close', this.closePODAddPopup);
    this.$bus.$off('view-shipment-popup-close', this.closeShipmentViewPopup);
  }
};
</script>

<style lang="scss" scoped>
</style>

